import type { SheetData, SheetColumn, SheetRow } from "../../engine/models";
import type { SheetCell } from "../../engine/cells";
import type { ApiBid, ApiLineGroup } from "./types";
import { lineGroupToSheetRows } from "./rowMappers";
import { applySpreadsheetStyles } from "../utils/spreadsheetStyles";
import type { Theme } from "@mui/material";
import { getColumnsForMode, type ColumnProfile } from "./columnProfiles";

export function bidToSheetData(
    bid: ApiBid,
    sheetName: string,
    isRunning: boolean,
    theme: Theme,
    expansions: Record<string, boolean>,
    columnFilter?: string[],
    highlightDepth?: number | null,
): SheetData {
    let profiles = getColumnsForMode(isRunning).filter(
        (c: ColumnProfile) => c.visible || c.alwaysVisible,
    );

    // Optional column filter — only show columns whose IDs are in the filter set
    if (columnFilter) {
        const filterSet = new Set([...columnFilter, "_order"]); // _order is always included
        profiles = profiles.filter((p) => filterSet.has(p.id));
    }

    const columns: SheetColumn[] = profiles.map((p: ColumnProfile) => ({
        columnId: p.id,
        width: p.width || p.defaultWidth || 100,
        title: p.headerText || "",
    }));

    const rows: SheetRow[] = [];

    // Header row — uses custom iconHeader template with actual MUI icons
    const headerCells: SheetCell[] = profiles.map((p: ColumnProfile) => {
        if (p.headerIcon) {
            return {
                type: "iconHeader",
                text: p.headerText || "",
                iconId: p.headerIcon,
                nonEditable: true,
                style: {
                    fontWeight: "bold",
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                },
            } as any;
        }
        // Columns without icons (e.g. _order) use plain header
        return {
            type: "header",
            text: p.headerText || "",
            nonEditable: true,
            style: {
                fontWeight: "bold",
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
            },
        };
    });

    rows.push({
        rowId: "grid-header",
        cells: headerCells,
        height: 32,
    });

    // Spacer between header and first section
    rows.push({
        rowId: "spacer-header",
        height: 8,
        indentLevel: 0,
        cells: columns.map(() => ({
            type: "header",
            text: "",
            style: { background: "transparent" },
        })),
    });

    const rootGroups: ApiLineGroup[] = [];
    const groupMap = new Map<number, ApiLineGroup>();

    const baseGroups = isRunning
        ? (bid as any).line_groups_running
        : bid.line_groups;
    const baseItems = isRunning
        ? (bid as any).line_items_running
        : (bid as any).line_items;

    if (baseGroups) {
        baseGroups.forEach((g: any) => {
            groupMap.set(g.id, {
                ...g,
                children: [],
                line_items: [],
                addons: [],
            });
        });

        if (baseItems) {
            baseItems.forEach((item: any) => {
                if (item.line_group_id) {
                    const group = groupMap.get(item.line_group_id);
                    if (group) group.line_items.push(item);
                }
            });
        }

        if (bid.addons) {
            bid.addons.forEach((addon: any) => {
                if (addon.line_group_id) {
                    const group = groupMap.get(addon.line_group_id);
                    if (group) group.addons.push(addon);
                }
            });
        }

        baseGroups.forEach((g: any) => {
            const mappedGroup = groupMap.get(g.id)!;
            if (g.parent_id) {
                const parent = groupMap.get(g.parent_id);
                if (parent) {
                    parent.children!.push(mappedGroup);
                } else {
                    rootGroups.push(mappedGroup);
                }
            } else {
                rootGroups.push(mappedGroup);
            }
        });

        groupMap.forEach((v) => {
            if (v.children)
                v.children.sort(
                    (a: any, b: any) =>
                        (a.sort_order || 0) - (b.sort_order || 0),
                );
            if (v.line_items)
                v.line_items.sort(
                    (a: any, b: any) =>
                        (a.sort_order || 0) - (b.sort_order || 0),
                );
        });
        rootGroups.sort(
            (a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0),
        );
    }

    rootGroups.forEach((group) => {
        rows.push(...lineGroupToSheetRows(group, columns, undefined, 0));

        // Spacer row between sections — transparent, no border
        rows.push({
            rowId: `spacer-${group.id}`,
            height: 8,
            indentLevel: 0,
            cells: columns.map(() => ({
                type: "header",
                text: "",
                style: { background: "transparent" },
            })),
        });
    });

    const rootActionCells: SheetCell[] = profiles.map((p: ColumnProfile) => {
        const baseId = p.id.includes(".") ? p.id.split(".").pop()! : p.id;

        if (baseId === "name") {
            return {
                type: "addButton",
                parentMetadata: { id: 0 },
                rowType: "section",
                indentLevel: 0,
                text: "",
                value: 0,
            };
        }
        return { type: "header", text: "" };
    });

    rows.push({
        rowId: "action-root-add-group",
        height: 32,
        indentLevel: 0,
        cells: rootActionCells,
    });

    // ── Status dot: inject theme bg + highlight ───────────────────────────────
    // Ensure EVERY _order cell is statusDot so the overflow background fill
    // covers the full left margin on all rows (header, spacer, action included).
    const orderColIdx = columns.findIndex((c) => c.columnId === "_order");
    if (orderColIdx >= 0) {
        const defaultBg = theme.palette.background.default;
        rows.forEach((row) => {
            const cell = row.cells[orderColIdx];
            if (!cell) return;

            // Convert non-statusDot cells (header in spacer/action rows) to statusDot
            if (cell.type !== "statusDot") {
                row.cells[orderColIdx] = {
                    type: "statusDot",
                    nonEditable: true,
                    style: cell.style,
                    bgColor: defaultBg,
                } as any;
            } else {
                (cell as any).bgColor = defaultBg;
                if (
                    highlightDepth != null &&
                    row.indentLevel === highlightDepth &&
                    row.metadata?.type
                ) {
                    (cell as any).showDot = true;
                }
            }
        });
    }

    const finalRows = applySpreadsheetStyles(
        rows,
        columns,
        theme,
        bid.status !== "Drafts",
        isRunning,
        expansions,
    );

    return {
        name: sheetName,
        columns,
        rows: finalRows,
    };
}
